import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Briefcase, MapPin, AlertTriangle, CheckCircle2 } from "lucide-react";

// ============================================================================
// PublicApplyLanding — Phase H (pt.39, 2026-08-26).
// Public entry point for job-board / QR-code applicants. Fetches
// posting metadata, captures optional prefill contact info + a
// hidden honeypot + a page-load timestamp, then POSTs to the
// public start endpoint. On success, redirects to the existing
// tokened intake form at /hiring/intake/{token_id}.
// ============================================================================

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function PublicApplyLanding() {
  const { tenantSlug, postingSlug } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [state, setState] = useState({ loading: true, error: null });
  const [form, setForm] = useState({ name: "", email: "", phone: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const loadedAtMs = useRef(Date.now());

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const { data } = await axios.get(
        `${BACKEND}/api/public/postings/${tenantSlug}/${postingSlug}`,
      );
      setMeta(data);
      loadedAtMs.current = Date.now();
      setState({ loading: false, error: null });
    } catch (e) {
      const notFound = e?.response?.status === 404;
      setState({
        loading: false,
        error: notFound
          ? "This posting is no longer accepting applications."
          : "Something went wrong loading this posting. Please try again shortly.",
      });
    }
  }, [tenantSlug, postingSlug]);

  useEffect(() => { load(); }, [load]);

  async function submit(e) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const { data } = await axios.post(
        `${BACKEND}/api/public/apply/${tenantSlug}/${postingSlug}/start`,
        {
          name: form.name.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website,   // honeypot — real users leave blank
          t_loaded_at_ms: loadedAtMs.current,
        },
      );
      // Honeypot path — backend returns 200 with `intake_url: null`.
      // Show fake success so bots don't learn they were caught.
      if (!data.intake_url) {
        navigate(`/apply/${tenantSlug}/${postingSlug}/thanks`);
        return;
      }
      navigate(data.intake_url);
    } catch (e2) {
      const msg = e2?.response?.data?.detail
        || "Couldn't start your application. Please try again shortly.";
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[#4b5563]">
        Loading posting…
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3" data-testid="public-apply-error">
          <AlertTriangle className="w-8 h-8 text-[#b45309] mx-auto" />
          <div className="text-lg font-medium text-[#0e1420]">{state.error}</div>
          <div className="text-sm text-[#4b5563]">
            If you believe this posting should be live, please email the
            hiring team directly.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] py-10 px-4">
      <div className="max-w-2xl mx-auto bg-white border border-[#e5e7eb] rounded-lg p-8 shadow-sm"
           data-testid="public-apply-landing">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-[#4b5563] mb-1">Now hiring</div>
          <h1 className="text-3xl font-semibold text-[#0e1420]">
            {meta.position_title}
          </h1>
          <div className="mt-2 flex flex-wrap gap-4 text-sm text-[#4b5563]">
            {meta.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-4 h-4" />{meta.location}
              </span>
            )}
            <span className="inline-flex items-center gap-1">
              <Briefcase className="w-4 h-4" />{meta.tenant_name || "Mr. Electric"}
            </span>
          </div>
        </div>

        <div className="text-sm text-[#4b5563] mb-6">
          Ready to apply? We'll walk you through a quick set of questions —
          takes about 5 minutes. Basic contact info first, then a few
          role-specific questions. Nothing is shared publicly.
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                Full name (optional)
              </label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jane Doe"
                data-testid="public-apply-name"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
                Email (optional)
              </label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="you@example.com"
                data-testid="public-apply-email"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-[#4b5563] mb-1">
              Phone (optional)
            </label>
            <Input
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="(555) 555-5555"
              data-testid="public-apply-phone"
            />
          </div>
          {/* Honeypot: hidden from humans (CSS + a11y hint), visible to naive bots */}
          <div
            aria-hidden="true"
            style={{ position: "absolute", left: "-9999px", top: "-9999px" }}
          >
            <label htmlFor="website">Website (leave blank)</label>
            <input
              id="website"
              tabIndex={-1}
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              data-testid="public-apply-honeypot"
            />
          </div>

          {submitError && (
            <div
              className="text-sm text-[#991b1b] bg-[#fee2e2] border border-[#f87171] rounded p-3"
              data-testid="public-apply-submit-error"
            >
              {submitError}
            </div>
          )}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full"
            data-testid="public-apply-start-btn"
          >
            {submitting ? "Starting…" : "Start application"}
          </Button>
          <div className="text-xs text-[#4b5563] text-center">
            By continuing you'll be taken to the full intake form.
          </div>
        </form>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PublicApplyThanks — served on the honeypot fake-success path and also
// safe to reach directly. Kept visually indistinguishable from a real
// success state so basic bots do not learn they were flagged.
// ---------------------------------------------------------------------------
export function PublicApplyThanks() {
  return (
    <div className="min-h-screen bg-[#f8fafc] py-10 px-4">
      <div
        className="max-w-2xl mx-auto bg-white border border-[#e5e7eb] rounded-lg p-8 shadow-sm text-center"
        data-testid="public-apply-thanks"
      >
        <CheckCircle2 className="w-10 h-10 text-[#059669] mx-auto mb-3" />
        <h1 className="text-2xl font-semibold text-[#0e1420] mb-2">
          Thanks — we'll be in touch
        </h1>
        <div className="text-sm text-[#4b5563]">
          Your application has been received. If you're a fit, our hiring team
          will reach out shortly with next steps.
        </div>
      </div>
    </div>
  );
}
